import React, { useState, useEffect, useId } from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { applyProgressiveMask, cleanDigits } from '../utils/maskUtils';
import { isValidCPF } from '../utils/validators';

export interface CpfInputProps {
  value: string | null | undefined;
  onChange: (value: string, raw?: string, isValid?: boolean) => void;
  onValidChange?: (isValid: boolean) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  id?: string;
  name?: string;
  error?: string;
  helperText?: string;
  autoFocus?: boolean;
  showStatusIcon?: boolean;
  showErrorMessage?: boolean;
  onBlur?: () => void;
  'data-testid'?: string;
}

export default function CpfInput({
  value,
  onChange,
  onValidChange,
  label,
  required = false,
  disabled = false,
  className = '',
  placeholder = '000.000.000-00',
  id,
  name,
  error: externalError,
  helperText,
  autoFocus = false,
  showStatusIcon = true,
  showErrorMessage = true,
  onBlur,
  'data-testid': testId,
}: CpfInputProps) {
  const generatedId = useId();
  const inputId = id || generatedId;

  const [touched, setTouched] = useState(false);
  const [internalValue, setInternalValue] = useState(() => applyProgressiveMask('cpf', value));

  useEffect(() => {
    const formatted = applyProgressiveMask('cpf', value);
    setInternalValue(formatted);
  }, [value]);

  const clean = cleanDigits(internalValue);
  const isComplete = clean.length === 11;
  const isMathematicallyValid = isComplete ? isValidCPF(clean) : false;

  // Notificar callback externo de validade quando o status mudar
  useEffect(() => {
    if (onValidChange) {
      onValidChange(isMathematicallyValid);
    }
  }, [isMathematicallyValid, onValidChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value;
    const masked = applyProgressiveMask('cpf', rawInput);
    const rawClean = cleanDigits(rawInput);
    const valid = rawClean.length === 11 ? isValidCPF(rawClean) : false;

    setInternalValue(masked);
    onChange(masked, rawClean, valid);
  };

  const handleBlur = () => {
    setTouched(true);
    if (onBlur) {
      onBlur();
    }
  };

  // Determinar mensagem de erro interna
  let validationError: string | null = null;
  if (externalError) {
    validationError = externalError;
  } else if (isComplete && !isMathematicallyValid) {
    validationError = 'CPF inválido. Verifique os números digitados.';
  } else if (touched && clean.length > 0 && clean.length < 11) {
    validationError = 'CPF incompleto (deve conter 11 dígitos).';
  } else if (touched && required && clean.length === 0) {
    validationError = 'O CPF é obrigatório.';
  }

  const isInvalid = Boolean(validationError);
  const isValid = isComplete && isMathematicallyValid;

  // Estilos de borda e foco
  let borderClasses = 'border-slate-300 focus:border-primary-500 focus:ring-primary-500/20';
  if (isInvalid) {
    borderClasses = 'border-red-500 text-red-900 focus:border-red-600 focus:ring-red-500/20';
  } else if (isValid) {
    borderClasses = 'border-emerald-500 text-slate-900 focus:border-emerald-600 focus:ring-emerald-500/20';
  }

  return (
    <div className={`flex flex-col space-y-1 ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center select-none"
        >
          <span>{label}</span>
          {required && (
            <span
              className="text-red-500 font-bold ml-1"
              title="Campo obrigatório"
              aria-hidden="true"
            >
              *
            </span>
          )}
        </label>
      )}

      <div className="relative">
        <input
          id={inputId}
          name={name}
          type="text"
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          maxLength={14}
          data-testid={testId}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? `${inputId}-error` : undefined}
          className={`w-full px-3 py-2 border rounded-lg text-sm font-mono transition-all outline-none focus:ring-2 disabled:bg-slate-100 disabled:text-slate-500 ${
            showStatusIcon ? 'pr-9' : ''
          } ${borderClasses}`}
        />

        {showStatusIcon && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center">
            {isValid && (
              <span title="CPF válido" className="flex items-center">
                <CheckCircle2
                  className="w-4 h-4 text-emerald-600 animate-in fade-in zoom-in-75 duration-200"
                />
              </span>
            )}
            {isInvalid && (
              <span title="CPF inválido" className="flex items-center">
                <AlertCircle
                  className="w-4 h-4 text-red-500 animate-in fade-in zoom-in-75 duration-200"
                />
              </span>
            )}
          </div>
        )}
      </div>

      {showErrorMessage && validationError && (
        <p
          id={`${inputId}-error`}
          className="text-xs text-red-600 mt-0.5 font-medium flex items-center gap-1 animate-in fade-in duration-150"
          role="alert"
        >
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{validationError}</span>
        </p>
      )}

      {helperText && !validationError && (
        <p className="text-xs text-slate-500 mt-0.5">{helperText}</p>
      )}
    </div>
  );
}
